/**
 * StyleSelector Component
 * 
 * Reusable component for selecting style variants.
 * Displays options as radio buttons with labels.
 */

import React from 'react';
import { Check } from 'lucide-react';

export interface StyleOption {
  id: string;
  name: string;
  description?: string;
}

export interface StyleSelectorProps {
  /** Category label (e.g., "OnOff", "Brightness") */
  label: string;
  /** Available style options */
  options: StyleOption[];
  /** Currently selected style ID */
  value: string;
  /** Callback when selection changes */
  onChange: (styleId: string) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Collapsible section (default: true) */
  collapsible?: boolean;
  /** Initial collapsed state */
  defaultCollapsed?: boolean;
}

export const StyleSelector: React.FC<StyleSelectorProps> = ({
  label,
  options,
  value,
  onChange,
  disabled = false,
  collapsible = false,
  defaultCollapsed = false
}) => {
  const [isCollapsed, setIsCollapsed] = React.useState(defaultCollapsed);

  if (options.length === 0) {
    return null;
  }

  return (
    <div className="mb-4">
      {/* Label */}
      <div 
        className={`flex items-center gap-2 mb-2 ${collapsible ? 'cursor-pointer select-none' : ''}`}
        onClick={() => collapsible && setIsCollapsed(!isCollapsed)}
      >
        {collapsible && (
          <svg 
            className={`w-4 h-4 text-slate-400 transition-transform ${isCollapsed ? '' : 'rotate-90'}`}
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        )}
        <span className="text-sm font-medium text-slate-300">{label}</span>
        <span className="text-xs text-slate-500">
          ({options.find(o => o.id === value)?.name || value})
        </span>
      </div>

      {/* Options */}
      {!isCollapsed && (
        <div className="flex flex-wrap gap-2 ml-6">
          {options.map((option) => {
            const isSelected = option.id === value;
            return (
              <button
                key={option.id}
                onClick={() => !disabled && onChange(option.id)}
                disabled={disabled}
                className={`
                  relative px-3 py-1.5 rounded-lg text-xs font-medium transition-all
                  ${isSelected 
                    ? 'bg-blue-600 text-white border border-blue-500' 
                    : 'bg-slate-800/50 text-slate-300 border border-slate-700 hover:border-slate-600 hover:bg-slate-700/50'
                  }
                  ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
                title={option.description}
              >
                <span className="flex items-center gap-1.5">
                  {isSelected && <Check className="w-3 h-3" />}
                  {option.name}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * StyleSelectorGroup Component
 * 
 * Groups multiple StyleSelectors under a section header.
 */
export interface StyleSelectorGroupProps {
  /** Section title */
  title: string;
  /** Children StyleSelector components */
  children: React.ReactNode;
  /** Whether the group is collapsible */
  collapsible?: boolean;
  /** Initial collapsed state */
  defaultCollapsed?: boolean;
}

export const StyleSelectorGroup: React.FC<StyleSelectorGroupProps> = ({
  title,
  children,
  collapsible = true,
  defaultCollapsed = false
}) => {
  const [isCollapsed, setIsCollapsed] = React.useState(defaultCollapsed);

  return (
    <div className="mb-6">
      {/* Section Header */}
      <div 
        className={`flex items-center gap-2 mb-3 pb-2 border-b border-slate-700/50 ${collapsible ? 'cursor-pointer select-none' : ''}`}
        onClick={() => collapsible && setIsCollapsed(!isCollapsed)}
      >
        {collapsible && (
          <svg 
            className={`w-4 h-4 text-slate-400 transition-transform ${isCollapsed ? '' : 'rotate-90'}`}
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        )}
        <h4 className="text-sm font-semibold text-white">{title}</h4>
      </div>

      {/* Content */}
      {!isCollapsed && (
        <div className="space-y-3">
          {children}
        </div>
      )}
    </div>
  );
};

export default StyleSelector;
